import {
  useDeletePoster,
  usePosters,
  useRegeneratePoster,
  useSelectPosterVersion,
} from '../api/hooks';
import type { Match, Tournament } from '../api/types';
import ErrorBanner from './ErrorBanner';
import PosterCard from './PosterCard';

/**
 * Every poster made for this competition. Generating one starts from a fixture, on the Fixtures
 * tab, because the opponent, date, venue and selected side all come from it.
 */
export default function PosterGallery({
  tournament,
  matches,
  onGoToFixtures,
}: {
  tournament: Tournament;
  matches: Match[];
  onGoToFixtures: () => void;
}) {
  const posters = usePosters(tournament.id);
  const regenerate = useRegeneratePoster(tournament.id);
  const deletePoster = useDeletePoster(tournament.id);
  const selectVersion = useSelectPosterVersion(tournament.id);

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h2>Posters</h2>
          <p className="muted">
            Posters are generated from a fixture, so its opponent, date and selected side are on the
            poster.
          </p>
        </div>
        <button type="button" className="button button--primary" onClick={onGoToFixtures}>
          Go to fixtures
        </button>
      </div>

      <ErrorBanner error={posters.error} />

      {posters.data?.length === 0 && (
        <div className="card empty">
          <p className="muted">
            No posters for this competition yet.{' '}
            {matches.length === 0
              ? 'Add a fixture first, then generate a poster from it.'
              : 'Open the Fixtures tab and generate one from a fixture.'}
          </p>
        </div>
      )}

      <div className="grid grid--cards">
        {posters.data?.map((poster) => (
          <PosterCard
            key={poster.id}
            poster={poster}
            match={matches.find((m) => m.id === poster.matchId)}
            tournamentName={tournament.name}
            onRegenerate={() => void regenerate.mutateAsync(poster.id)}
            onSelectVersion={(versionId) =>
              void selectVersion.mutateAsync({ posterId: poster.id, versionId })
            }
            onDelete={() => {
              if (window.confirm('Delete this poster?')) {
                void deletePoster.mutateAsync(poster.id);
              }
            }}
          />
        ))}
      </div>
    </div>
  );
}
